import React from 'react';
import { Card, CardContent } from '@/components/ui/card';

const About = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
                L'histoire de <span className="text-gradient">Pablo Coiffure</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Créé par Pablo Domingos, passionné de coiffure depuis son plus jeune âge, 
                Pablo Coiffure est né d'un rêve : offrir à chaque client une expérience élégante, moderne et sur mesure.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Avec un style unique alliant technicité, précision et créativité, Pablo sublime aussi bien les hommes que les femmes, 
                en respectant l'identité et la personnalité de chacun.
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="/images/IMG-20251103-WA0025.jpg"
                alt="Pablo Domingos - Fondateur de Pablo Coiffure"
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-sm font-medium">Pablo Domingos</p>
                <p className="text-xs text-gray-300">Fondateur & Maître Coiffeur</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pablo's Story */}
      <section className="py-20 bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="heading-xl font-display mb-6">
              Rencontrez <span className="text-gradient">Pablo</span>
            </h2>
            <p className="text-enhanced max-w-3xl mx-auto">
              L'homme derrière la légende. Découvrez l'histoire passionnante de Pablo Domingos, 
              le maître coiffeur qui a révolutionné l'art capillaire à Lyon.
            </p>
          </div>

          {/* Histoire principale avec photo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Photo de Pablo avec cadre élégant */}
            <div className="lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary-glow/20 rounded-3xl blur-lg"></div>
                <div className="relative bg-gradient-to-br from-secondary to-background p-6 rounded-2xl shadow-2xl">
                  <img 
                    src="/images/pablo-fondateur.jpg"
                    alt="Pablo Domingos - Fondateur de Pablo Coiffure"
                    className="rounded-xl w-full h-96 object-cover shadow-lg"
                  />
                  <div className="absolute inset-6 bg-gradient-to-t from-black/70 via-transparent to-transparent rounded-xl"></div>
                  <div className="absolute bottom-10 left-10 text-white">
                    <h3 className="text-2xl font-bold mb-2">Pablo Domingos</h3>
                    <p className="text-primary font-semibold text-lg">Kuendambasi Kiangebeni Domingos</p>
                    <p className="text-sm text-gray-300 mt-2">Fondateur & Maître Coiffeur depuis 2019</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Histoire de Pablo */}
            <div className="lg:order-1">
              <div className="space-y-8">
                {/* Citation inspirante */}
                <div className="bg-gradient-to-r from-primary/10 to-primary-glow/10 p-8 rounded-2xl border border-primary/20">
                  <blockquote className="text-2xl font-display italic text-center mb-4">
                    "Chaque client qui entre dans mon salon mérite de ressortir transformé, 
                    confiant et rayonnant. C'est ma mission, ma passion, ma vie."
                  </blockquote>
                  <p className="text-primary font-bold text-center text-lg">- Pablo Domingos</p>
                </div>

                {/* Histoire personnelle */}
                <Card className="card-luxury">
                  <CardContent className="p-8">
                    <h3 className="heading-md font-display mb-6 text-center">
                      L'histoire de <span className="text-gradient">Pablo</span>
                    </h3>
              
                    <div className="space-y-6">
                      {/* Parcours personnel */}
                      <div className="bg-gradient-to-r from-secondary/50 to-background p-6 rounded-xl border border-primary/10">
                        <h4 className="text-lg font-semibold text-primary mb-3">Le début d'une passion</h4>
                        <p className="text-enhanced">
                          Né <strong className="text-white">Kuendambasi Kiangebeni Domingos</strong>, Pablo découvre sa passion pour la coiffure dès l'adolescence. 
                          Fasciné par l'art de transformer les cheveux, il passe des heures à observer les techniques des maîtres coiffeurs 
                          et à perfectionner son savoir-faire sur ses proches.
                        </p>
                      </div>
                    
                      {/* Formation et expertise */}
                      <div className="bg-gradient-to-r from-secondary/50 to-background p-6 rounded-xl border border-primary/10">
                        <h4 className="text-lg font-semibold text-primary mb-3">Formation et expertise</h4>
                        <p className="text-enhanced">
                          Chez Pablo Coiffure, chaque coupe, chaque tresse et chaque soin est une œuvre d'art. 
                          Derrière ce nom devenu une référence à Lyon se cache <strong className="text-primary">Kuendambasi Kiangebeni Domingos</strong>, 
                          plus connu sous le nom de Pablo, un coiffeur passionné qui a fait de son talent un véritable style de vie.
                        </p>
                      </div>
                      
                      {/* Spécialisation */}
                      <div className="bg-gradient-to-r from-secondary/50 to-background p-6 rounded-xl border border-primary/10">
                        <h4 className="text-lg font-semibold text-primary mb-3">Spécialisation unique</h4>
                        <p className="text-enhanced">
                          <strong className="text-white">Sa passion pour la coiffure est née très jeune</strong> — une passion qu'il a transformée en métier d'excellence, 
                          en alliant <span className="text-primary font-semibold">créativité, précision technique et amour du détail</span>. 
                          Spécialiste des cheveux afro et métissés, Pablo maîtrise toutes les techniques modernes et traditionnelles.
                        </p>
                      </div>
                      
                      {/* Philosophie */}
                      <div className="bg-gradient-to-r from-secondary/50 to-background p-6 rounded-xl border border-primary/10">
                        <h4 className="text-lg font-semibold text-primary mb-3">Sa philosophie</h4>
                        <p className="text-enhanced">
                          Ce qui distingue Pablo, c'est sa capacité à <strong className="text-white">comprendre le style et la personnalité de chaque client</strong>.
                          Il ne fait pas seulement des coupes, il crée des looks qui inspirent la confiance et l'élégance. 
                          <strong className="text-white">De la coupe masculine la plus précise aux tresses féminines les plus raffinées</strong>, en passant par la mise en beauté des ongles, 
                          Pablo et son équipe offrent une expérience complète dans une ambiance <span className="text-primary font-semibold">conviviale et raffinée</span>.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          
          {/* Statistiques impressionnantes */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-16">
            <div className="text-center p-6 bg-gradient-to-br from-primary/10 to-primary-glow/10 rounded-xl border border-primary/20">
              <div className="text-4xl font-bold text-primary mb-2">5+</div>
              <div className="text-sm text-muted-foreground font-medium">Années d'expérience</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-primary/10 to-primary-glow/10 rounded-xl border border-primary/20">
              <div className="text-4xl font-bold text-primary mb-2">24h/24</div>
              <div className="text-sm text-muted-foreground font-medium">Service disponible</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-primary/10 to-primary-glow/10 rounded-xl border border-primary/20">
              <div className="text-4xl font-bold text-primary mb-2">500+</div>
              <div className="text-sm text-muted-foreground font-medium">Clients satisfaits</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-primary/10 to-primary-glow/10 rounded-xl border border-primary/20">
              <div className="text-4xl font-bold text-primary mb-2">100%</div>
              <div className="text-sm text-muted-foreground font-medium">Passion et dévouement</div>
            </div>
          </div>
        </div>
      </section>

      {/* Salon Images */}
      <section className="py-16 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Découvrez notre <span className="text-gradient">salon</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Un espace moderne et chaleureux au cœur de Lyon
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "/images/IMG-20251103-WA0023.jpg",
              "/images/IMG-20251103-WA0025.jpg",
              "/images/IMG-20251103-WA0026.jpg",
              "/images/IMG-20251103-WA0027.jpg",
              "/images/IMG-20251103-WA0028.jpg",
              "/images/IMG-20251103-WA0029.jpg"
            ].map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-lg">
                <img 
                  src={image}
                  alt={`Salon Pablo Coiffure ${index + 1}`}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Nos <span className="text-gradient">valeurs</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Excellence",
                description: "Nous nous engageons à offrir des prestations de la plus haute qualité, avec des produits professionnels et des techniques maîtrisées."
              },
              {
                title: "Créativité",
                description: "Chaque client est unique. Nous créons des looks personnalisés qui reflètent votre personnalité et subliment votre beauté naturelle."
              },
              {
                title: "Passion",
                description: "Notre amour pour l'art de la coiffure se ressent dans chaque geste, chaque coupe, chaque création que nous réalisons."
              }
            ].map((value, index) => (
              <Card key={index} className="card-luxury text-center">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-primary mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-gradient-to-r from-primary/10 to-primary-glow/10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Venez nous <span className="text-gradient">rencontrer</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Pablo et son équipe vous accueillent dans un cadre moderne et chaleureux, 
            ouvert 24h/24 du lundi au samedi pour s'adapter à votre emploi du temps.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold rounded-lg hover:shadow-xl hover:shadow-primary/30 transform hover:scale-105 transition-all duration-300"
            >
              Nous contacter
            </a>
            <a 
              href="/booking"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold rounded-lg transform hover:scale-105 transition-all duration-300"
            >
              Prendre rendez-vous
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;